import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { cr } from '@/lib/db';
import { useAuth } from '@/lib/auth';
import { UploadCloud, Eye, Check, AlertTriangle, X, Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';

// ============================================================================
// EvidenceTab — private Photos/Evidence workflow for a case.
// Uploads go to the private asc-case-evidence bucket (MFA-gated storage policy);
// metadata rows to cr.case_evidence_assets (RLS: MFA + case access). Viewing
// requests a short-lived signed URL from the asc-evidence-signed-url edge
// function, which authorizes + audits the access server-side. No public URLs.
// ============================================================================

const BUCKET = 'asc-case-evidence';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const STAGES = ['pre_op', 'intra_op', 'pacu', 'discharge', 'post_op', 'admin'] as const;
const CATEGORIES = ['site_mark', 'implant', 'specimen', 'equipment', 'document', 'clinical_photo', 'other'] as const;
const LATERALITY = ['', 'left', 'right', 'bilateral', 'midline', 'not_applicable'] as const;
const SOURCES = ['camera', 'upload', 'scanner', 'device_import'] as const;
const REVIEW_FILTERS = ['all', 'pending', 'accepted', 'needs_review', 'rejected'] as const;

interface EvidenceAsset {
    id: string;
    stage: string;
    category: string;
    body_site: string | null;
    laterality: string | null;
    capture_source: string;
    mime_type: string;
    caption: string | null;
    review_status: string;
    captured_at: string;
    storage_path: string;
}

const REVIEW_BADGE: Record<string, string> = {
    pending: 'bg-pt-amber/15 text-pt-amber border-pt-amber/30',
    accepted: 'bg-pt-green/15 text-pt-green border-pt-green/30',
    needs_review: 'bg-pt-cyan/15 text-pt-cyan border-pt-cyan/30',
    rejected: 'bg-pt-red/15 text-pt-red border-pt-red/30',
};

async function sha256Hex(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function requestSignedUrl(assetId: string, reason: string): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/asc-evidence-signed-url`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: ANON_KEY,
        },
        body: JSON.stringify({ asset_id: assetId, reason }),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { signed_url?: string };
    return j.signed_url ?? null;
}

export default function EvidenceTab({ caseId, orgId, patientId, encounterId }: {
    caseId: number; orgId: string; patientId: number | null; encounterId: number | null;
}) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const [filter, setFilter] = useState<(typeof REVIEW_FILTERS)[number]>('all');
    const [showCapture, setShowCapture] = useState(false);
    const [preview, setPreview] = useState<{ url: string; mime: string } | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const assetsQuery = useQuery({
        queryKey: ['case-evidence', caseId],
        queryFn: async (): Promise<EvidenceAsset[]> => {
            const { data, error } = await cr().from('case_evidence_assets')
                .select('id, stage, category, body_site, laterality, capture_source, mime_type, caption, review_status, captured_at, storage_path')
                .eq('case_id', caseId)
                .order('captured_at', { ascending: false });
            if (error) throw error;
            return (data ?? []) as EvidenceAsset[];
        },
    });

    const assets = assetsQuery.data ?? [];
    const filtered = useMemo(
        () => (filter === 'all' ? assets : assets.filter((a) => a.review_status === filter)),
        [assets, filter]
    );
    const pendingCount = useMemo(() => assets.filter((a) => a.review_status === 'pending').length, [assets]);

    async function handleView(a: EvidenceAsset) {
        setBusyId(a.id);
        try {
            const url = await requestSignedUrl(a.id, 'workspace_view');
            if (!url) { alert('Could not open this asset.'); return; }
            if (a.mime_type.startsWith('image/')) setPreview({ url, mime: a.mime_type });
            else window.open(url, '_blank', 'noopener');
        } finally {
            setBusyId(null);
        }
    }

    async function handleReview(a: EvidenceAsset, status: 'accepted' | 'needs_review' | 'rejected') {
        setBusyId(a.id);
        try {
            const { error } = await cr().from('case_evidence_assets')
                .update({ review_status: status, reviewed_at: new Date().toISOString(), reviewed_by: user?.id ?? null })
                .eq('id', a.id);
            if (error) { alert('Review update failed.'); return; }
            await qc.invalidateQueries({ queryKey: ['case-evidence', caseId] });
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className="max-w-5xl">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-1">
                    {REVIEW_FILTERS.map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => setFilter(f)}
                            className={[
                                'px-2.5 py-1 text-xs rounded-md border transition-colors',
                                filter === f ? 'border-pt-cyan text-pt-text bg-pt-cyan/10' : 'border-pt-input-border text-pt-text-muted hover:text-pt-text',
                            ].join(' ')}
                        >
                            {f === 'all' ? 'All' : f.replace('_', ' ')}
                            {f === 'pending' && pendingCount > 0 && <span className="ml-1 text-pt-amber">{pendingCount}</span>}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => setShowCapture((v) => !v)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-pt-gold text-pt-darker text-sm font-semibold [&>svg]:w-4 [&>svg]:h-4"
                >
                    <UploadCloud /> {showCapture ? 'Close' : 'Capture / import'}
                </button>
            </div>

            {showCapture && (
                <CaptureForm
                    caseId={caseId} orgId={orgId} patientId={patientId} encounterId={encounterId}
                    onDone={async () => { setShowCapture(false); await qc.invalidateQueries({ queryKey: ['case-evidence', caseId] }); }}
                />
            )}

            {assetsQuery.isLoading ? (
                <div className="text-sm text-pt-text-dim py-6"><span className="pt-spinner mr-2" />Loading evidence…</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    title={filter === 'all' ? 'No evidence captured for this case' : `No evidence in "${filter.replace('_', ' ')}"`}
                    hint={filter === 'all'
                        ? 'Photos and documents captured here upload to encrypted case storage and are audited on every view.'
                        : 'Change the review filter to see the rest of this case\u2019s evidence.'}
                    action={filter === 'all' ? (
                        <button
                            type="button"
                            onClick={() => setShowCapture(true)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-pt-gold text-pt-darker text-sm font-semibold [&>svg]:w-4 [&>svg]:h-4"
                        >
                            <UploadCloud /> Capture / import
                        </button>
                    ) : undefined}
                />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((a) => (
                        <div key={a.id} className="bg-pt-panel border border-pt-border rounded-md p-3 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-pt-text-muted">
                                    {a.category.replace('_', ' ')}
                                </span>
                                <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border ${REVIEW_BADGE[a.review_status] ?? ''}`}>
                                    {a.review_status.replace('_', ' ')}
                                </span>
                            </div>
                            <div className="text-xs text-pt-text-muted">
                                {a.stage.replace('_', '-')}
                                {a.body_site ? ` · ${a.body_site}` : ''}
                                {a.laterality && a.laterality !== 'not_applicable' ? ` · ${a.laterality}` : ''}
                            </div>
                            {a.caption && <div className="text-sm truncate">{a.caption}</div>}
                            <div className="text-[11px] text-pt-text-dim font-mono">
                                {new Date(a.captured_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                                <IconBtn onClick={() => handleView(a)} busy={busyId === a.id} title="View (audited)"><Eye /></IconBtn>
                                <IconBtn onClick={() => handleReview(a, 'accepted')} title="Accept"><Check /></IconBtn>
                                <IconBtn onClick={() => handleReview(a, 'needs_review')} title="Needs review"><AlertTriangle /></IconBtn>
                                <IconBtn onClick={() => handleReview(a, 'rejected')} title="Reject"><X /></IconBtn>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {preview && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
                    <div className="max-w-3xl max-h-full" onClick={(e) => e.stopPropagation()}>
                        <img src={preview.url} alt="Evidence preview" className="max-w-full max-h-[80vh] rounded-md border border-pt-border" />
                        <button type="button" onClick={() => setPreview(null)} className="mt-3 text-sm text-pt-text-muted hover:text-pt-text">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function IconBtn({ children, onClick, title, busy }: { children: ReactNode; onClick: () => void; title: string; busy?: boolean }) {
    return (
        <button
            type="button"
            title={title}
            disabled={busy}
            onClick={onClick}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-pt-dark border border-pt-input-border text-pt-text-muted hover:text-pt-text hover:border-pt-cyan/40 disabled:opacity-50 [&>svg]:w-4 [&>svg]:h-4"
        >
            {busy ? <Loader2 className="animate-spin" /> : children}
        </button>
    );
}

function CaptureForm({ caseId, orgId, patientId, encounterId, onDone }: {
    caseId: number; orgId: string; patientId: number | null; encounterId: number | null; onDone: () => Promise<void>;
}) {
    const [file, setFile] = useState<File | null>(null);
    const [stage, setStage] = useState<(typeof STAGES)[number]>('intra_op');
    const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('clinical_photo');
    const [bodySite, setBodySite] = useState('');
    const [laterality, setLaterality] = useState<(typeof LATERALITY)[number]>('');
    const [source, setSource] = useState<(typeof SOURCES)[number]>('camera');
    const [caption, setCaption] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function submit() {
        if (!file) { setErr('Select a file first.'); return; }
        setBusy(true); setErr(null);
        try {
            const safe = file.name.replace(/[^\w.\-]/g, '_');
            const path = `${orgId}/${caseId}/${crypto.randomUUID()}_${safe}`;
            const sha256 = await sha256Hex(file);

            const { error: upErr } = await supabase.storage
                .from(BUCKET).upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
            if (upErr) { setErr(`Upload failed: ${upErr.message}`); return; }

            const { error: insErr } = await cr().from('case_evidence_assets').insert({
                org_id: orgId,
                case_id: caseId,
                patient_id: patientId,
                encounter_id: encounterId,
                stage,
                category,
                body_site: bodySite || null,
                laterality: laterality || null,
                capture_source: source,
                storage_bucket: BUCKET,
                storage_path: path,
                mime_type: file.type || 'application/octet-stream',
                sha256,
                caption: caption || null,
            });
            if (insErr) { setErr(`Save failed: ${insErr.message}`); return; }

            setFile(null); setBodySite(''); setCaption('');
            await onDone();
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="bg-pt-panel border border-pt-border rounded-md p-4 mb-4 space-y-3">
            <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-pt-text-muted file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-pt-dark file:text-pt-text file:text-sm"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Select label="Stage" value={stage} onChange={(v) => setStage(v as (typeof STAGES)[number])} options={STAGES} />
                <Select label="Category" value={category} onChange={(v) => setCategory(v as (typeof CATEGORIES)[number])} options={CATEGORIES} />
                <Select label="Source" value={source} onChange={(v) => setSource(v as (typeof SOURCES)[number])} options={SOURCES} />
                <label className="text-xs text-pt-text-muted">
                    Body site
                    <input value={bodySite} onChange={(e) => setBodySite(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text" />
                </label>
                <Select label="Laterality" value={laterality} onChange={(v) => setLaterality(v as (typeof LATERALITY)[number])} options={LATERALITY} />
                <label className="text-xs text-pt-text-muted">
                    Caption
                    <input value={caption} onChange={(e) => setCaption(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text" />
                </label>
            </div>
            {err && <div className="text-sm text-pt-red">{err}</div>}
            <button
                type="button"
                onClick={submit}
                disabled={busy || !file}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-pt-gold text-pt-darker text-sm font-semibold disabled:opacity-50 [&>svg]:w-4 [&>svg]:h-4"
            >
                {busy ? <Loader2 className="animate-spin" /> : <UploadCloud />} Upload to case
            </button>
        </div>
    );
}

function Select({ label, value, onChange, options }: {
    label: string; value: string; onChange: (v: string) => void; options: readonly string[];
}) {
    return (
        <label className="text-xs text-pt-text-muted">
            {label}
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full px-2 py-1.5 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text"
            >
                {options.map((o) => (
                    <option key={o} value={o}>{o === '' ? '—' : o.replace('_', ' ')}</option>
                ))}
            </select>
        </label>
    );
}
